import { constants as offerReduxConstants } from '@makemydeal/dr-offer-redux';
import { ApplicationCodes, FlexBuyAvailableMonths } from '@makemydeal/dr-platform-types';
import { ROLL_TO_OPTIONS, ROLL_TO_OPTIONS_ADJUSTMENT_TYPE, ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';
import {
    UPDATE_ROLL_TARGET_BALANCE_DUE,
    UPDATE_ROLL_TARGET_CASH_DUE,
    UPDATE_ROLL_TARGET_PAYMENT,
    UPDATE_ROLL_TARGET_PROFIT
} from './actionTypes/rollToOptionsActionTypes';

export const DEALER_ROLE = 'dealer';
export const SHOPPER_ROLE = 'shopper';

// Manual Incentives Types
export const DEALER_CASH = 'Dealer Cash';
export const INCLUDED = 'Included';
export const LENDER = 'Lender';
export const MANUFACTURER = 'Manufacturer';

export const VIN_SOURCE = 'VIN';

export const SHOPPER_QUOTE_DASHBOARD = 'shopper-quote-dashboard';
export const REGULAR = 'regular'; // For any feature with regular dashboard
export const SHARE_URL_OPTION_LABEL = 'Share URL';
export const SHARE_EMAIL_OPTION_LABEL = 'Share via Email';
export const SHARE_URL_INPUT_LABEL = 'Share URL';
export const COPY_URL_BUTTON_TEXT = 'Copy';
export const SHARE_MESSAGE_PLACEHOLDER = 'Include a message about this offer';
export const SHOPPER_EMAIL_INPUT_LABEL = 'Shopper Email Address';

export const ICO = 'ICO';
export const SD = 'SD';
export const USER = 'USER';

export const ICO_MESSAGE = 'KBB ICO';
export const SD_MESSAGE = 'Standard KBB';
export const USER_MESSAGE = 'Shopper Submitted Value';
export const DEFAULT_MESSAGE = 'Dealer';

// analytics related
export const DATA_LAYER_NAME = 'dataLayer';

export const CDL_USER_INTENT = {
    ASP_CHECKBOX_TOGGLED: 'asp-checkbox-toggled',
    PAYMENT_GRID_TOGGLED: 'payment-grid-toggled',
    PAYMENT_GRID_SELL_RATE: 'payment-grid-sell-rate',
    PAYMENT_GRID_RESIDUAL: 'payment-grid-residual',
    PAYMENT_GRID_TEXT_ENTRY: 'payment-grid-text-entry',
    // A grid cell click. Its own intent rather than a borrowed text-entry one, so the analytics events keyed on
    // PAYMENT_GRID_TEXT_ENTRY cannot start matching a selection if their action gates change.
    PAYMENT_GRID_CELL_SELECTED: 'payment-grid-cell-selected'
} as const;

export const ACQUISITION_FEE_ID = 1050;
export const LOAN_ORIGINATION_FEE_ID = 1051;
/** Duplicated from `activities-desking/src/constants.ts` — that lib depends on this one, not the reverse. */
export const PRO_RATA_FEE_TYPE_ID = 1056;

//push to darwin
export const PUSH_TO_DARWIN_FLOW = 'PUSH_TO_DARWIN_FLOW';
export const OPEN_IN_DARWIN_FLOW = 'OPEN_IN_DARWIN_FLOW';
export const EMPTY_IN_DARWIN_FLOW = 'EMPTY_IN_DARWIN_FLOW';
export const enum DARWIN_ERROR_MESSAGES {
    PUSH_TO_DARWIN_ERROR = 'Darwin is unavailable at this time.',
    OPEN_IN_DARWIN_ERROR = 'Please check that you are signed into Darwin and the system is available then try again.',
    STOCK_NUMBER_ERROR = 'Please provide a valid stock number and try again.'
}

// Roll To Options SlideOut
export const ROLL_TO_OPTIONS_ROOT = 'rollToOptions';

/**
 * Maps each Roll To tab label to the roll option the payment request is keyed by.
 *
 * FRONT_PROFIT is a display-only label and never becomes the active tab, but this map and
 * ROLL_TO_TARGET_FIELD_MAP below stay total over the enum so an unexpected label still resolves to the
 * profit roll instead of silently producing an `undefined` key.
 */
export const ROLL_TO_OPTION_BY_TAB: Record<ROLL_TO_OPTIONS_LABELS, ROLL_TO_OPTIONS> = {
    [ROLL_TO_OPTIONS_LABELS.PAYMENT]: ROLL_TO_OPTIONS.PAYMENT,
    [ROLL_TO_OPTIONS_LABELS.BALANCE_DUE]: ROLL_TO_OPTIONS.BALANCE_DUE,
    [ROLL_TO_OPTIONS_LABELS.CASH_DUE]: ROLL_TO_OPTIONS.CASH_DUE,
    [ROLL_TO_OPTIONS_LABELS.PROFIT]: ROLL_TO_OPTIONS.PROFIT,
    [ROLL_TO_OPTIONS_LABELS.FRONT_PROFIT]: ROLL_TO_OPTIONS.PROFIT
};

/**
 * Maps each Roll To tab label to its corresponding target field name.
 * This mapping is used across action creators and reducers to maintain consistency
 * between tab labels and their associated target value fields.
 */
export const ROLL_TO_TARGET_FIELD_MAP = {
    [ROLL_TO_OPTIONS_LABELS.PAYMENT]: 'targetPayment',
    [ROLL_TO_OPTIONS_LABELS.BALANCE_DUE]: 'targetBalanceDue',
    [ROLL_TO_OPTIONS_LABELS.CASH_DUE]: 'targetCashDue',
    [ROLL_TO_OPTIONS_LABELS.PROFIT]: 'targetProfit',
    [ROLL_TO_OPTIONS_LABELS.FRONT_PROFIT]: 'targetProfit'
} as const;

export type RollToTargetField = (typeof ROLL_TO_TARGET_FIELD_MAP)[keyof typeof ROLL_TO_TARGET_FIELD_MAP];

/**
 * Maps each Roll To option (the payment-request roll key) to the target field the UPR response is stamped with.
 * Keyed by option rather than tab label, which is what the roll request itself carries.
 */
export const ROLL_TO_TARGET_FIELD_BY_OPTION: Record<ROLL_TO_OPTIONS, RollToTargetField> = {
    [ROLL_TO_OPTIONS.PAYMENT]: 'targetPayment',
    [ROLL_TO_OPTIONS.BALANCE_DUE]: 'targetBalanceDue',
    [ROLL_TO_OPTIONS.CASH_DUE]: 'targetCashDue',
    [ROLL_TO_OPTIONS.PROFIT]: 'targetProfit'
};

/**
 * Maps each Roll To option to the action type that records the user-entered target value for that option.
 * Typed as the action-type union rather than `string` so dispatching a lookup result stays checked against
 * `RollToOptionsAction`.
 */
export const UPDATE_ROLL_TARGET_ACTION_TYPE_BY_OPTION: Record<
    ROLL_TO_OPTIONS,
    | typeof UPDATE_ROLL_TARGET_PAYMENT
    | typeof UPDATE_ROLL_TARGET_BALANCE_DUE
    | typeof UPDATE_ROLL_TARGET_CASH_DUE
    | typeof UPDATE_ROLL_TARGET_PROFIT
> = {
    [ROLL_TO_OPTIONS.PAYMENT]: UPDATE_ROLL_TARGET_PAYMENT,
    [ROLL_TO_OPTIONS.BALANCE_DUE]: UPDATE_ROLL_TARGET_BALANCE_DUE,
    [ROLL_TO_OPTIONS.CASH_DUE]: UPDATE_ROLL_TARGET_CASH_DUE,
    [ROLL_TO_OPTIONS.PROFIT]: UPDATE_ROLL_TARGET_PROFIT
};

/**
 * Maps each Roll To tab label to the adjustment type reported as the roll's target.
 * Both profit tabs report the same profit adjustment type.
 */
export const ROLL_TO_ADJUSTMENT_TYPE_BY_TAB: Record<ROLL_TO_OPTIONS_LABELS, ROLL_TO_OPTIONS_ADJUSTMENT_TYPE> = {
    [ROLL_TO_OPTIONS_LABELS.PAYMENT]: ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.MONTHLY_PAYMENT,
    [ROLL_TO_OPTIONS_LABELS.BALANCE_DUE]: ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.BALANCE_DUE,
    [ROLL_TO_OPTIONS_LABELS.CASH_DUE]: ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.CASH_DUE,
    [ROLL_TO_OPTIONS_LABELS.PROFIT]: ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.PROFIT,
    [ROLL_TO_OPTIONS_LABELS.FRONT_PROFIT]: ROLL_TO_OPTIONS_ADJUSTMENT_TYPE.PROFIT
};

// Due At Signing SlideOut
export const DUE_AT_SIGNING_EDIT = 'dueAtSigning';

// Due At Signing
export const SUMMARY_DUE_AT_SIGNING = 'summaryDueAtSigning';
export const DAS_OVERRIDES_NODE = 'overrides';
export const DAS_PAYMENT_NODE = 'payment';

// push to FI
export const PUSH_TO_FI_FLOW = 'PUSH_TO_FI_FLOW';
export const PUSH_TO_FI_INITIATED_PAGE_ACTION = 'MV:PushToFI:Initiated';
export const PUSH_TO_FI_RESULT_PAGE_ACTION = 'MV:PushToFI:Result';
export const PUSH_TO_FI_DEAL_REF_ID_TIMEOUT_MS = 10_000;
export const FS_SYNC_FINAL_ATTEMPT_TIMEOUT_MS = 600_000;

// push to dms
export const PUSH_TO_DMS_FLOW = 'PUSH_TO_DMS_FLOW';
export const CDMS = 'CDMS';
export const DMS_PLUS = 'DMS+';
export const DMS_DEALAPI_ONLY = 'DEALAPI_ONLY';
export const FIIA_DMS_INTEGRATIONS = [DMS_PLUS, DMS_DEALAPI_ONLY];

// in-Manager-View dealership selection
export const DEALERSHIP_SELECTION_FLOW = 'DEALERSHIP_SELECTION_FLOW';
export const COX_DMS_PLUS = 'CoxDMS+';

export const PERSONA_WHITE_LIST = ['shopper', 'dealer', 'system'];
export const APP_NAME_BLACKLIST = ['vin leads api'];

export const PERSONA_APPNAME_RELATION = {
    dealmaker: ApplicationCodes.MV,
    dealer: 'Showroom',
    shopper: 'Accelerate My Deal',
    customer: 'Customer View',
    system: 'System'
} as Record<string, string>;
export const CREDIT_APPLICATION = 'creditApplication';

export const VERSION_CREATED_BY_TO_APP_DISPLAY_NAME = {
    DarwinFI: 'Darwin',
    'CAI-Deal-Change-Processor-Service': 'VinCRM',
    'VIN CRM': 'VinCRM'
} as Record<string, string>;

// VinCRM-relayed events carry versionCreatedBy.applicationName 'VIN CRM' (compared case-insensitively).
export const VIN_CRM_APPLICATION_NAME = 'vin crm';

// Common Consumer-relayed events carry versionCreatedBy.applicationName 'Common Consumer' (compared case-insensitively).
export const COMMON_CONSUMER_APPLICATION_NAME = 'common consumer';

// F280192: `offer.applicationName` values marking a deal as CRM-originated. 'VIN Leads API' is what a
// real VinCRM launch carries; VIN_CRM_APPLICATION_NAME is the spelling the socket path uses.
export const VIN_LEADS_API_APPLICATION_NAME = 'vin leads api';

// US2127847: Deal List "Create New Deal" renders open-deal's NewDealModule inside Deal Central, and
// open-deal-bff's DealBuilder stamps the new deal 'Open Deal' (open-deal-bff/src/constants.ts).
export const OPEN_DEAL_APPLICATION_NAME = 'open deal';

// US2150197: a deal the salesperson sent from Sales Check-In; gated by F319397_First_Pencil_SCI_Request.
export const SALES_CHECK_IN_SOURCE_APPLICATION_NAME = 'sales check-in';

// Entry points eligible for the intercept: Entry 1 (VinCRM launch), Entry 2 (Deal List) and Sales Check-In.
export const FIRST_PENCIL_SOURCE_APP_NAMES = [
    VIN_LEADS_API_APPLICATION_NAME,
    VIN_CRM_APPLICATION_NAME,
    OPEN_DEAL_APPLICATION_NAME,
    SALES_CHECK_IN_SOURCE_APPLICATION_NAME
];

export const DARWIN_APPLICATION_NAME = 'DarwinFI';

// Digital contracting document add/replace patches carry a stale changedBy and should not trigger GTL.
export const DIGITAL_CONTRACTING_APP_NAME = 'digital contracting';
export const IGNORED_CONTRACTING_PATCH_TYPES = ['add-documents', 'replace-documents'];

export const DEFAULT_DAYS_TO_FIRST_PAYMENT = 30;

export const { CALC_ENDPOINTS } = offerReduxConstants;

export const PROGRAM_LOOKUP_EDIT_META = 'programLookupEdit';

export const RESTORED_ACTIVE_CELL_META = 'restoredActiveCell';

export const INCENTIVE_SLIDEOUT_INCLUDED = 'included';
export const INCENTIVE_SLIDEOUT_MANUAL_UPDATES = 'manualUpdates';

export const SOURCE_TYPE_DEALER = 'DEALER';
export const ALLOWANCE_LABEL = 'Allowance';
export const ALLOWANCE_SOURCE_LABEL_MANAGER = 'Updated by Manager';
export const ALLOWANCE_SOURCE_LABEL_SALESPERSON = 'Updated by Salesperson';

export const ELIMINATED_FEE_AMOUNT = 0;

export const ACCESSORIES = 'Accessories';

export const DAS_OVERRIDES_FIELDS = {
    DOWNPAYMENT: 'downPayment',
    FEES_CAPITALIZED: 'feesCapitalized',
    TAXES_CAPITALIZED: 'taxesCapitalized',
    TARGET_CASH_AMOUNT_PAID: 'targetCashAmountPaid',
    FIRST_PAYMENT_CAPITALIZED: 'firstPaymentCapitalized',
    EQUITY_ALLOCATION_METHOD: 'equityAllocationMethod'
};

export const BASE64_STRING_VALIDATION_REGEX = /^[A-Za-z0-9+/]*(=|==)?$/;
export const BASE64_STRING_ERROR_MESSAGE = 'Invalid base64 data received';
export const DOWNLOAD_AUDIT_LOG_MISSING_URL_ERROR_MESSAGE = 'Download audit log response did not include URL';

export const FLEX_BUY_TERM_LIST = [FlexBuyAvailableMonths.MONTH_66, FlexBuyAvailableMonths.MONTH_75];
export const EQUITY_ALLOCATION_METHOD = 'equityAllocationMethod';

// Date format constant (duplicated from dash-components to prevent circular dependency)
export const DATE_FORMAT = 'MM/dd/yyyy';

// Alert message displayed after a credit app has been added to the deal via the Find and Merge flow
export const ATTACH_CREDIT_APP_SAVE_REMINDER =
    'A new credit application has been selected for this deal. Save the deal to complete the attachment, merge deal information, and initiate document transfer. Unsaved changes will be lost if not saved.';

export const DOC_MOVE_INITIATED_MESSAGE =
    'Document move in progress. This may take a moment. Verify completion by checking the Digital Deal Jacket.';
export const DOC_MOVE_SUCCESS_MESSAGE = 'Documents successfully moved to new deal jacket.';
export const DOC_MOVE_FAILURE_MESSAGE =
    'Document move failed. Please upload missing documents via Manage Documents in the deal jacket.';

export const FUEL_SAVINGS_BAGGAGE_NAME = 'deal360.fuelSavings';
export const DEAL_COMPARISON_BAGGAGE_NAME = 'deal360.comparison';
export const MV_VPP_OVERRIDES_BAGGAGE_NAME = 'mv.vppOverrides';
